import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChatBubbleOutlineRounded,
  DeleteOutlineRounded,
  DescriptionOutlined,
  EditOutlined,
  EmailOutlined,
  GroupOutlined,
  PhoneOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';
import { Dialog } from '../../../../../ui';
import { editApplicantNote } from '../../../../../utils/api_service/JobOpenings';
import { ConversationNote, NoteStructure } from '../../../../../utils/types/JobOpenings';
import { DIALOG_TYPE } from '../../../Constants/DialogTypes';

export interface INotesTabProps {
  notes?: ConversationNote[];
  onDelete: (id: string) => Promise<void>;
  getJobApplicants: () => Promise<void>;
  isModalOpen: boolean;
  setIsModalOpen: (value: ((prevState: boolean) => boolean) | boolean) => void;
}

const CONVERSATION_CONFIG: Record<
  string,
  { label: string; icon: SvgIconComponent; color: string; bg: string }
> = {
  Email: {
    label: 'Email',
    icon: EmailOutlined,
    color: '#2563eb',
    bg: 'rgba(37, 99, 235, 0.1)',
  },
  Phone: {
    label: 'Phone Call',
    icon: PhoneOutlined,
    color: '#16a34a',
    bg: 'rgba(22, 163, 74, 0.1)',
  },
  Person: {
    label: 'In-Person',
    icon: GroupOutlined,
    color: '#9333ea',
    bg: 'rgba(147, 51, 234, 0.1)',
  },
  Video: {
    label: 'Video Call',
    icon: VideocamOutlined,
    color: '#4f46e5',
    bg: 'rgba(79, 70, 229, 0.1)',
  },
  Other: {
    label: 'Other',
    icon: DescriptionOutlined,
    color: '#64748b',
    bg: 'rgba(100, 116, 139, 0.1)',
  },
};

export const NotesTab = ({
  notes,
  onDelete,
  getJobApplicants,
  isModalOpen,
  setIsModalOpen,
}: INotesTabProps) => {
  const notesData = notes || [];
  const hasNotes = notesData.length > 0;
  const userEmail = localStorage.getItem('email');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string>('');

  const [editedNote, setEditedNote] = useState<NoteStructure>({
    noteBody: '',
    noteSubject: '',
    noteType: '',
  });

  const formatDate = (timestamp: string) => {
    const unix = Number(timestamp);
    if (isNaN(unix)) return 'Invalid date';

    const date = new Date(unix * 1000);
    const day = String(date.getDate()).padStart(2, '0');
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const handleEdit = (note: ConversationNote) => {
    setEditingNoteId(note.id);
    setEditedNote({
      noteBody: note.noteBody,
      noteSubject: note.noteSubject,
      noteType: note.noteType,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = async (note: ConversationNote) => {
    try {
      const response = await editApplicantNote(note.id, editedNote);
      if (response.ok) {
        await getJobApplicants();
      }
    } catch (e) {
      console.error('Failed to edit note:', e);
    }
    setEditingNoteId(null);
  };

  const handleCancel = () => {
    setEditingNoteId(null);
  };

  if (!hasNotes) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
          borderRadius: '16px',
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          my: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'rgba(217, 119, 6, 0.08)',
            color: '#d97706',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <ChatBubbleOutlineRounded sx={{ fontSize: 28 }} />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
        >
          No Conversation Notes
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto' }}
        >
          No notes have been logged for this candidate yet. Use &quot;Add Note&quot; to begin.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              p: 0.75,
              borderRadius: '8px',
              bgcolor: 'rgba(217, 119, 6, 0.1)',
              color: '#d97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChatBubbleOutlineRounded sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: '15px', color: 'text.primary' }}
          >
            Conversation History
          </Typography>
        </Stack>
        <Chip
          label={`${notesData.length} Note${notesData.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '11px',
            bgcolor: 'grey.100',
            color: 'text.secondary',
          }}
        />
      </Stack>

      {/* Notes List */}
      <Stack spacing={2}>
        {notesData.map((note: ConversationNote) => {
          const config =
            CONVERSATION_CONFIG[note.noteType] || CONVERSATION_CONFIG.Other;
          const IconComp = config.icon;
          const isEditing = editingNoteId === note.id;

          return (
            <Paper
              key={note.id}
              elevation={0}
              sx={{
                borderRadius: '12px',
                border: '1px solid',
                borderColor: isEditing ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {/* Note Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      bgcolor: config.bg,
                      color: config.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IconComp sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editedNote.noteSubject}
                        onChange={(e) =>
                          setEditedNote((prev) => ({
                            ...prev,
                            noteSubject: e.target.value,
                          }))
                        }
                        sx={{ bgcolor: 'background.paper' }}
                      />
                    ) : (
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}
                      >
                        {note.noteSubject || 'Conversation Note'}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontSize: '11.5px', display: 'block' }}
                    >
                      Logged by {note.author?.firstName ? `${note.author.firstName} ${note.author.lastName}` : note.author?.email || 'Recruiter'}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isEditing ? (
                    <FormControl size="small">
                      <Select
                        value={editedNote.noteType}
                        onChange={(e) =>
                          setEditedNote((prev) => ({
                            ...prev,
                            noteType: e.target.value,
                          }))
                        }
                        sx={{ bgcolor: 'background.paper', fontSize: '12px', height: '32px' }}
                      >
                        {Object.entries(CONVERSATION_CONFIG).map(([key, item]) => (
                          <MenuItem key={key} value={key} sx={{ fontSize: '12px' }}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '11px',
                        bgcolor: config.bg,
                        color: config.color,
                        border: '1px solid',
                        borderColor: config.color + '40',
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Note Content */}
              <Box sx={{ p: 2 }}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedNote.noteBody}
                    onChange={(e) =>
                      setEditedNote((prev) => ({
                        ...prev,
                        noteBody: e.target.value,
                      }))
                    }
                    placeholder="Enter conversation note details..."
                    sx={{ fontSize: '13px' }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line',
                      fontSize: '13px',
                    }}
                  >
                    {note.noteBody}
                  </Typography>
                )}
              </Box>

              {/* Note Footer */}
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: 'grey.50',
                  borderTop: '1px solid',
                  borderColor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                  Added on {formatDate(note.createdAt)}
                </Typography>

                {note.author?.email === userEmail && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isEditing ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleSave(note)}
                          sx={{ textTransform: 'none', fontSize: '11.5px', py: 0.5, px: 1.5 }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={handleCancel}
                          sx={{ textTransform: 'none', fontSize: '11.5px', py: 0.5, px: 1.5 }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit Note">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(note)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                          >
                            <EditOutlined sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Note">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteId(note.id);
                              setIsModalOpen(true);
                            }}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutlineRounded sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                )}
              </Box>
            </Paper>
          );
        })}
      </Stack>

      <Dialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Are you sure you want to delete this note?"
        dialogType={DIALOG_TYPE.ERROR}
        justify="center"
        confirmButtonLable="Delete Conversation Note"
        cancelButtonLable="Cancel"
        handleApply={() => onDelete(deleteId)}
      >
        <></>
      </Dialog>
    </Box>
  );
};
